import type { MembershipMemberRow } from '../../api/membership.api';
import { resolveThemeToken } from '../../themeTokens';

type Props = {
  members: MembershipMemberRow[];
  themeTokenByTypeUuid?: Record<string, string>;
  isLoading?: boolean;
  onView: (membershipUuid: string) => void;
};

const NULL_SYMBOL = '—';

const formatDateOrRelative = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  const days = Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return '1 month ago';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatExpiry = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const resolveBucketStatus = (member: MembershipMemberRow): 'active' | 'expiring' | 'expired' | 'revoked' => {
  const status = String(member.status || '').toUpperCase();
  if (status === 'REVOKED') return 'revoked';
  if (status === 'EXPIRED') return 'expired';
  if (!member.expiresAt) return 'active';
  const expiresAt = new Date(member.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return 'active';
  const daysUntilExpiry = (expiresAt - Date.now()) / (1000 * 60 * 60 * 24);
  if (daysUntilExpiry <= 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'expiring';
  return 'active';
};

const initials = (name: string | null | undefined): string => {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || safe[0]!.toUpperCase();
};

const MembersTable = ({ members, themeTokenByTypeUuid, isLoading, onView }: Props) => {
  if (!isLoading && members.length === 0) {
    return (
      <div className="members-table__empty" data-testid="members-table-empty">
        No members match the current filters.
      </div>
    );
  }

  return (
    <div className="members-table" data-testid="members-table">
      <table className="members-table__table">
        <thead>
          <tr>
            <th scope="col" className="members-table__col-member">MEMBER</th>
            <th scope="col" className="members-table__col-tier">TIER</th>
            <th scope="col" className="members-table__col-num">POINTS</th>
            <th scope="col" className="members-table__col-num">VISITS</th>
            <th scope="col" className="members-table__col-num">PACK</th>
            <th scope="col" className="members-table__col">LAST VISIT</th>
            <th scope="col" className="members-table__col">EXPIRES</th>
            <th scope="col" className="members-table__col">STATUS</th>
            <th scope="col" className="members-table__col-action" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const themeToken = themeTokenByTypeUuid?.[member.membershipTypeUuid];
            const theme = resolveThemeToken(themeToken);
            const bucket = resolveBucketStatus(member);
            const statusLabel = bucket === 'revoked' ? 'REVOKED' : bucket.toUpperCase();
            return (
              <tr key={member.membershipUuid} data-testid={`members-row-${member.membershipUuid}`}>
                <td className="members-table__member">
                  <span className="members-table__avatar" aria-hidden="true">
                    {initials(member.userName)}
                  </span>
                  <span className="members-table__member-text">
                    <span className="members-table__member-name">{member.userName || NULL_SYMBOL}</span>
                    <span className="members-table__member-phone">{member.phone || NULL_SYMBOL}</span>
                  </span>
                </td>
                <td className="members-table__tier">
                  <span className="members-table__tier-pill">
                    <span
                      className="members-table__tier-dot"
                      style={{ background: theme.swatchHex }}
                      aria-hidden="true"
                    />
                    {member.membershipTypeName || NULL_SYMBOL}
                  </span>
                </td>
                <td className="members-table__num">{Number(member.pointsBalance ?? 0).toLocaleString('en-IN')}</td>
                <td className="members-table__num">{Number(member.totalVisits ?? 0)}</td>
                <td className="members-table__num">
                  {String(member.benefitType) === 'VISITS' && (member.includedVisits ?? 0) > 0
                    ? `${Number(member.visitsUsed ?? 0)}/${Number(member.includedVisits)}`
                    : NULL_SYMBOL}
                </td>
                <td>{member.lastVisitedAt ? formatDateOrRelative(member.lastVisitedAt) : 'Never'}</td>
                <td className="members-table__expires">{formatExpiry(member.expiresAt)}</td>
                <td>
                  <span
                    className={`members-table__status members-table__status--${bucket}`}
                    data-testid={`members-status-pill-${member.membershipUuid}`}
                  >
                    {statusLabel}
                  </span>
                </td>
                <td className="members-table__action">
                  <button
                    type="button"
                    className="members-table__view"
                    onClick={() => onView(member.membershipUuid)}
                    data-testid={`members-view-${member.membershipUuid}`}
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MembersTable;
